import { PD2, PTR } from 'common';
import Data from 'data';
import { itemCreateNormal } from './itemReducers';

export function isSubTypeEx(type, ...types) {
  if (types.includes(type)) return true;
  const info = Data.itemTypes[type];
  return info && ((info.equiv1 && isSubTypeEx(info.equiv1, ...types)) || (info.equiv2 && isSubTypeEx(info.equiv2, ...types)));
}

export function isSubType(type, what) {
  if (type === what) return true;
  const info = Data.itemTypes[type];
  return info && ((info.equiv1 && isSubType(info.equiv1, what)) || (info.equiv2 && isSubType(info.equiv2, what)));
}

export function isEquippedIn(type, loc) {
  const info = Data.itemTypes[type];
  return info && (info.bodyloc1 === loc || info.bodyloc2 === loc);
}

export const itemTypeNames = {
  shie: 'Shields',
  tors: 'Body Armor',
  gold: 'Gold',
  bowq: 'Arrows',
  xboq: 'Bolts',
  play: 'Player Body Parts',
  herb: 'Herbs',
  poti: 'Potions',
  ring: 'Rings',
  elix: 'Elixirs',
  amul: 'Amulets',
  char: 'Charms',
  boot: 'Boots',
  glov: 'Gloves',
  book: 'Books',
  belt: 'Belts',
  gem: 'Gems',
  torc: 'Torches',
  scro: 'Scrolls',
  scep: 'Scepters',
  wand: 'Wands',
  staf: 'Staves',
  bow: 'Bows',
  axe: 'Axes',
  club: 'Clubs',
  swor: 'Swords',
  hamm: 'Hammers',
  knif: 'Daggers',
  spea: 'Spears',
  pole: 'Polearms',
  xbow: 'Crossbows',
  mace: 'Maces',
  helm: 'Helmets',
  tpot: 'Throwing Potions',
  ques: 'Quest Items',
  body: 'Body Parts',
  key: 'Keys',
  tkni: 'Throwing Knives',
  taxe: 'Throwing Axes',
  jave: 'Javelins',
  weap: 'Weapons',
  mele: 'Melee Weapons',
  miss: 'Missile Weapons',
  thro: 'Thrown Weapons',
  comb: 'Thrown Weapons',
  armo: 'Armor',
  shld: 'Shields',
  misc: 'Miscellaneous',
  sock: 'Socket Fillers',
  seco: 'Off-hand Items',
  rod: 'Staves and Rods',
  misl: 'Ammunition',
  blun: 'Blunt Weapons',
  jewl: 'Jewels',
  amaz: 'Amazon Items',
  barb: 'Barbarian Items',
  necr: 'Necromancer Items',
  pala: 'Auric Shields', // this is only used on Exile runeword, it makes more sense to say shields here
  sorc: 'Sorceress Items',
  assn: 'Assassin Items',
  drui: 'Druid Items',
  h2h: 'Claws',
  orb: 'Orbs',
  head: 'Voodoo Heads',
  ashd: 'Auric Shields',
  phlm: 'Primal Helms',
  pelt: 'Pelts',
  cloa: 'Cloaks',
  rune: 'Runes',
  circ: 'Circlets',
  hpot: 'Healing Potions',
  mpot: 'Mana Potions',
  rpot: 'Rejuvenation Potions',
  spot: 'Stamina Potions',
  apot: 'Antidote Potions',
  wpot: 'Thawing Potions',
  scha: 'Small Charms',
  mcha: 'Large Charms',
  lcha: 'Grand Charms',
  abow: 'Amazon Bows',
  aspe: 'Amazon Spears',
  ajav: 'Amazon Javelins',
  h2h2: 'Claws',
  mboq: 'Magic Arrows',
  mxbq: 'Magic Bolts',
  gem0: 'Chipped Gems',
  gem1: 'Flawed Gems',
  gem2: 'Standard Gems',
  gem3: 'Flawless Gems',
  gem4: 'Perfect Gems',
  gema: 'Amethysts',
  gemd: 'Diamonds',
  geme: 'Emeralds',
  gemr: 'Rubies',
  gems: 'Sapphires',
  gemt: 'Topazes',
  gemz: 'Skulls',
};

const typeExact = (type, children) => ({name: itemTypeNames[type], filter: t => t === type, type, children});
const typeSubs = (type, children) => ({name: itemTypeNames[type], filter: t => isSubType(t, type), type, children});

const shieldType = typeSubs('shld', [
  typeExact('ashd'),
  typeExact('head'),
]);
const meleeType = typeSubs('mele', [
  typeSubs('axe', [
    typeExact('taxe'),
  ]),
  typeExact('swor'),
  typeSubs('knif', [
    typeExact('tkni'),
  ]),
  typeSubs('spea', [
    typeExact('aspe'),
    typeSubs('jave', [
      typeExact('ajav'),
    ]),
  ]),
  typeExact('pole'),
  typeExact('club'),
  typeExact('hamm'),
  typeExact('mace'),
  typeExact('scep'),
  typeExact('wand'),
  typeExact('staf'),
  typeSubs('h2h'),
]);
const missileType = typeSubs('miss', [
  typeSubs('bow', [
    typeExact('abow'),
  ]),
  typeExact('xbow'),
]);
const helmetTypes = [
  typeExact('circ'),
  typeExact('pelt'),
  typeExact('phlm'),
];
const charmTypes = [
  typeExact('scha'),
  typeExact('mcha'),
  typeExact('lcha'),
];
const comboType = typeSubs('comb', [
  typeExact('taxe'),
  typeExact('tkni'),
  typeSubs('jave', [
    typeExact('ajav'),
  ]),
]);

const weaponTypes = [
  shieldType,
  meleeType,
  missileType,
  typeExact('orb'),
  typeSubs('thro', [
    typeExact('taxe'),
    typeExact('tkni'),
    typeSubs('jave', [
      typeExact('ajav'),
    ]),
    typeExact('tpot'),
  ]),
];
const weaponTypesCreate = {
  name: 'Weapons and Shields',
  filter: t => isSubType(t, 'weap') || isSubType(t, 'shld'),
  type: 'weap',
  children: [
    shieldType,
    meleeType,
    missileType,
    typeExact('orb'),
    comboType,
  ],
};

const mercWeaponTypes = PD2 ? [
  typeSubs('bow', [
    typeExact('abow'),
  ]),
  typeExact('xbow'),
  typeSubs('spea', [
    typeExact('jave'),
  ]),
  typeExact('pole'),
  typeSubs('shie', [
    typeExact('ashd'),
  ]),
  typeExact('swor'),
  typeExact('wand'),
  typeExact('scep'),
  typeExact('mace'),
  typeSubs('knif', [
    typeExact('tkni'),
  ]),
  typeExact('orb'),
  typeSubs('axe', [
    typeExact('taxe'),
  ]),
  typeExact('hamm'),
] : [
  typeExact('shie'),
  typeExact('swor'),
  typeSubs('spea', [
    typeExact('jave'),
  ]),
  typeExact('pole'),
  (PTR ? (
    typeSubs('bow', [
      typeExact('abow'),
    ])
  ) : (
    typeExact('bow')
  )),
];

export const itemTypeTree = {
  name: `All Items`,
  filter: () => true,
  children: [
    {
      name: itemTypeNames.armo,
      separator: true,
    },
    typeSubs('helm', helmetTypes),
    typeExact('tors'),
    typeExact('glov'),
    typeExact('boot'),
    typeExact('belt'),
    shieldType,
    {
      name: 'Misc',
      separator: true,
    },
    typeExact('amul'),
    typeExact('ring'),
    typeExact('jewl'),
    typeSubs('char', charmTypes),
    {
      name: itemTypeNames.weap,
      separator: true,
    },
    meleeType,
    missileType,
    typeExact('orb'),
    comboType,
  ],
};

export const itemSlotGroups = {
  head: {
    name: 'Head',
    slot: 'head',
    filter: type => isEquippedIn(type, 'head'),
    hidden: true,
    children: helmetTypes,
    create: typeSubs('helm', helmetTypes),
  },
  neck: {
    name: 'Neck',
    slot: 'neck',
    filter: type => isEquippedIn(type, 'neck'),
    hidden: true,
    create: typeExact('amul'),
  },
  tors: {
    name: 'Torso',
    slot: 'tors',
    filter: type => isEquippedIn(type, 'tors'),
    hidden: true,
    create: typeExact('tors'),
  },
  rarm: {
    name: 'Right Hand',
    slot: 'rarm',
    filter: type => isEquippedIn(type, 'rarm'),
    hidden: true,
    children: weaponTypes,
    create: weaponTypesCreate,
  },
  larm: {
    name: 'Left Hand',
    slot: 'larm',
    filter: type => isEquippedIn(type, 'larm'),
    hidden: true,
    children: weaponTypes,
    create: weaponTypesCreate,
  },
  rarm2: {
    name: 'Right Hand (II)',
    slot: 'rarm2',
    filter: type => isEquippedIn(type, 'rarm'),
    hidden: true,
    children: weaponTypes,
    create: weaponTypesCreate,
  },
  larm2: {
    name: 'Left Hand (II)',
    slot: 'larm2',
    filter: type => isEquippedIn(type, 'larm'),
    hidden: true,
    children: weaponTypes,
    create: weaponTypesCreate,
  },
  rrin: {
    name: 'Right Ring',
    slot: 'rrin',
    filter: type => isEquippedIn(type, 'rrin'),
    hidden: true,
    create: typeExact('ring'),
  },
  lrin: {
    name: 'Left Ring',
    slot: 'lrin',
    filter: type => isEquippedIn(type, 'lrin'),
    hidden: true,
    create: typeExact('ring'),
  },
  belt: {
    name: 'Belt',
    slot: 'belt',
    filter: type => isEquippedIn(type, 'belt'),
    hidden: true,
    create: typeExact('belt'),
  },
  feet: {
    name: 'Feet',
    slot: 'feet',
    filter: type => isEquippedIn(type, 'feet'),
    hidden: true,
    create: typeExact('boot'),
  },
  glov: {
    name: 'Gloves',
    slot: 'glov',
    filter: type => isEquippedIn(type, 'glov'),
    hidden: true,
    create: typeExact('glov'),
  },
  inventory: {
    name: 'Inventory',
    slot: 'inventory',
    filter: type => true,//isSubType(type, 'char') || type === 'book' || type === 'ques',
    hidden: true,
    children: charmTypes,
    create: {
      name: 'Inventory',
      preset: true,
      filter: t => isSubType(t, 'char'),
      type: 'char',
      children: charmTypes,
    },
  },
  cube: {
    name: 'Cube',
    slot: 'cube',
    filter: type => true,//isSubType(type, 'char') || type === 'book' || type === 'ques',
    hidden: true,
    children: itemTypeTree.children,
    create: itemTypeTree,
  },
  merc_rarm: {
    name: 'Right Hand (Merc)',
    slot: 'rarm',
    merc: true,
    filter: type => isEquippedIn(type, 'rarm'),
    hidden: true,
    children: mercWeaponTypes,
    create: typeSubs('weap', mercWeaponTypes),
  },
  merc_larm: {
    name: 'Left Hand (Merc)',
    slot: 'larm',
    merc: true,
    filter: type => isEquippedIn(type, 'larm'),
    hidden: true,
    children: mercWeaponTypes,
    create: typeSubs('weap', mercWeaponTypes),
  },
  merc_head: {
    name: 'Head',
    slot: 'head',
    merc: true,
    filter: type => isEquippedIn(type, 'head'),
    hidden: true,
    children: helmetTypes,
    create: typeSubs('helm', helmetTypes),
  },
  merc_tors: {
    name: 'Torso',
    slot: 'tors',
    merc: true,
    filter: type => isEquippedIn(type, 'tors'),
    hidden: true,
    create: typeExact('tors'),
  },
  merc_glov: {
    name: 'Gloves',
    slot: 'glov',
    merc: true,
    filter: type => isEquippedIn(type, 'glov'),
    hidden: true,
    create: typeExact('glov'),
  },
  merc_feet: {
    name: 'Feet',
    slot: 'feet',
    merc: true,
    filter: type => isEquippedIn(type, 'feet'),
    hidden: true,
    create: typeExact('boot'),
  },
  merc_belt: {
    name: 'Belt',
    slot: 'belt',
    merc: true,
    filter: type => isEquippedIn(type, 'belt'),
    hidden: true,
    create: typeExact('belt'),
  },
};
for (const id in itemSlotGroups) itemSlotGroups[id].slotId = id;

export const otherHandSlot = {
  rarm: 'larm',
  larm: 'rarm',
  rarm2: 'larm2',
  larm2: 'rarm2',
};

const baseTypes = itemTypeTree.children.slice();
itemTypeTree.filter = type => baseTypes.some(t => !t.separator && t.filter(type));
itemTypeTree.children.push(...Object.values(itemSlotGroups));

export const craftingRecipes = {
  hitpower: 'Hit Power',
  blood: 'Blood',
  caster: 'Caster',
  safety: 'Safety',
  vampiric: 'Vampiric',
  bountiful: 'Bountiful',
  Hit: 'Hit Power',
  Blood: 'Blood',
  Caster: 'Caster',
  Safety: 'Safety',
};

const presetItemIds = [
  'aqv', // Arrows
  'cqv', // Bolts
  'tbk', // Town Portal Book
  'ibk', // Identify Book
  'box', // Horadric Cube
  'gps', // Rancid Gas Potion
  'ops', // Oil Potion
  'gpm', // Choking Gas Potion
  'opm', // Exploding Potion
  'gpl', // Strangling Gas Potion
  'opl', // Fulminating Potion
  'r01', 'r02', 'r03', 'r04', 'r05',
  'r06', 'r07', 'r08', 'r09', 'r10',
  'r11', 'r12', 'r13', 'r14', 'r15',
  'r16', 'r17', 'r18', 'r19', 'r20',
  'r21', 'r22', 'r23', 'r24', 'r25',
  'r26', 'r27', 'r28', 'r29', 'r30',
  'r31', 'r32', 'r33',
  'gcv', 'gfv', 'gsv', 'gzv', 'gpv', // Amethyst
  'gcy', 'gfy', 'gsy', 'gly', 'gpy', // Topaz
  'gcb', 'gfb', 'gsb', 'glb', 'gpb', // Saphire
  'gcg', 'gfg', 'gsg', 'glg', 'gpg', // Emerald
  'gcr', 'gfr', 'gsr', 'glr', 'gpr', // Ruby
  'gcw', 'gfw', 'gsw', 'glw', 'gpw', // Diamond
  'skc', 'skf', 'sku', 'skl', 'skz', // Skull
  'vps', 'yps', 'wms', // utility potions
  'tsc', 'isc', 'key', // scrolls and keys
  'hp1', 'hp2', 'hp3', 'hp4', 'hp5',
  'mp1', 'mp2', 'mp3', 'mp4', 'mp5',
  'rvs', 'rvl', // rejuvs
  'pk1', 'pk2', 'pk3', 'dhn', 'bey', 'mbr', // pandemonium items
  'toa', 'tes', 'ceh', 'bet', 'fed', // tokens
];
export const cubeOnlyItems = new Set([
  'pk1', 'pk2', 'pk3', 'dhn', 'bey', 'mbr', // pandemonium items
  'toa', 'tes', 'ceh', 'bet', 'fed', // tokens
]);

export const presetItems = {};

export function initPresets() {
  for (const id of presetItemIds) {
    presetItems[id] = itemCreateNormal(id);
  }
}
